import React from "react";
import { Link } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import SEO from "@/components/SEO";
import { ArrowRight } from "lucide-react";

const FrontaliersPage = () => {
  const { lang } = useApp();
  const title = {
    fr: "Calculateur pension Luxembourg pour frontaliers (FR · BE · DE)",
    en: "Luxembourg pension calculator for cross-border workers",
    de: "Luxemburger Rentenrechner für Grenzgänger",
  }[lang];
  const desc = {
    fr: "Plus de 220 000 frontaliers cotisent au Luxembourg. Estimez votre pension CNAP en quelques secondes, peu importe votre pays de résidence.",
    en: "Over 220,000 cross-border workers contribute in Luxembourg. Estimate your CNAP pension in seconds — no matter your country of residence.",
    de: "Über 220.000 Grenzgänger zahlen in Luxemburg ein. Schätzen Sie Ihre CNAP-Rente in Sekunden, unabhängig vom Wohnsitzland.",
  }[lang];

  const t = {
    fr: {
      h1: "Frontaliers — votre pension Luxembourg",
      lead: "Chaque année cotisée au Luxembourg ouvre des droits CNAP à vie. La coordination européenne (règlement 883/2004) garantit que vos années LU s'additionnent à votre carrière en France, Belgique ou Allemagne.",
      bullets: [
        "Pension versée directement sur votre compte, dans votre pays de résidence",
        "Imposée au Luxembourg à la source — convention bilatérale appliquée ensuite",
        "Études supérieures créditées comme périodes complémentaires",
        "Versement automatique entre 57 et 65 ans selon les conditions remplies",
      ],
      cta: "Estimer ma pension maintenant",
      learn: "Lire le guide complet pour frontaliers",
    },
    en: {
      h1: "Cross-border workers — your Luxembourg pension",
      lead: "Every year contributed in Luxembourg builds CNAP rights for life. EU coordination (regulation 883/2004) ensures your LU years add to your career in France, Belgium or Germany.",
      bullets: [
        "Pension paid directly to your bank account, in your country of residence",
        "Taxed at source in Luxembourg — bilateral treaty applied afterwards",
        "Higher-education years credited as complementary periods",
        "Automatic payment between 57 and 65 depending on conditions",
      ],
      cta: "Estimate my pension now",
      learn: "Read the full cross-border guide",
    },
    de: {
      h1: "Grenzgänger — Ihre Luxemburger Rente",
      lead: "Jedes in Luxemburg gezahlte Beitragsjahr begründet lebenslange CNAP-Ansprüche. Die EU-Koordinierung (Verordnung 883/2004) addiert Ihre LU-Jahre zu Ihrer Karriere in Frankreich, Belgien oder Deutschland.",
      bullets: [
        "Rente direkt auf Ihr Konto im Wohnsitzland",
        "In Luxemburg an der Quelle besteuert — danach bilateral angerechnet",
        "Hochschuljahre als Ergänzungszeiten anerkannt",
        "Automatische Auszahlung zwischen 57 und 65 je nach Bedingungen",
      ],
      cta: "Jetzt Rente schätzen",
      learn: "Den vollständigen Grenzgänger-Leitfaden lesen",
    },
  }[lang];

  return (
    <section className="max-w-5xl mx-auto px-6 md:px-8 py-16" data-testid="frontaliers-page">
      <SEO title={title} description={desc} path="/frontaliers" />
      <div className="border-b border-slate-200 pb-6 mb-10">
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-blue-700">/ frontaliers · cross-border · grenzgänger</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight text-slate-900 mt-2">{t.h1}</h1>
        <p className="text-base text-slate-600 leading-relaxed mt-6 max-w-3xl">{t.lead}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-slate-200 bg-white">
        {t.bullets.map((b, i) => (
          <div key={b} className={`p-6 md:p-8 ${i % 2 === 0 ? "md:border-r" : ""} ${i < 2 ? "md:border-b" : ""} border-slate-200`}>
            <div className="font-mono text-2xl text-blue-700">0{i + 1}</div>
            <p className="text-sm text-slate-700 leading-relaxed mt-3">{b}</p>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row gap-4 mt-10">
        <Link to="/?monthly_gross_salary=5000&current_age=40&career_start_age=30" data-testid="frontaliers-cta"
          className="px-6 py-3.5 bg-slate-900 text-white text-xs font-mono uppercase tracking-[0.2em] hover:bg-slate-800 inline-flex items-center gap-3">
          {t.cta} <ArrowRight size={14} strokeWidth={1.5} />
        </Link>
        <Link to="/blog/frontaliers-pension-luxembourg" data-testid="frontaliers-learn"
          className="px-6 py-3.5 bg-white border border-slate-300 text-slate-900 text-xs font-mono uppercase tracking-[0.2em] hover:bg-slate-50">
          {t.learn}
        </Link>
      </div>
    </section>
  );
};

export default FrontaliersPage;
